/**
 * Stores operated by the Fixa platform team as sandboxes. Each entry is a
 * synthetic store whose permissions are inherited from `baseStoreId` in
 * Åtkomst (Åtkomst has no node for the synthetic id itself).
 *
 * The full synthetic store id is `${baseStoreId}${suffix}`.
 *
 * Kept in this shared package so app-restructured and packages/reusable can
 * both consume it without crossing package boundaries.
 */
export const fixaTeamVirtualStores: ReadonlyArray<{
  baseStoreId: string;
  suffix: string;
}> = [
  { baseStoreId: "012", suffix: "#fixa-team" },
  { baseStoreId: "064", suffix: "#fixa-team" },
  { baseStoreId: "006", suffix: "#fixa-team" },
  { baseStoreId: "481", suffix: "$fixa-team" },
];

export const fixaTeamVirtualStoreIds = fixaTeamVirtualStores.map(
  ({ baseStoreId, suffix }) => `${baseStoreId}${suffix}`
);

const fixaTeamVirtualStoreSuffixes = Array.from(
  new Set(fixaTeamVirtualStores.map(({ suffix }) => suffix))
);

/**
 * Strip a known Fixa-team virtual-store suffix (e.g. `#fixa-team`,
 * `$fixa-team`) from a store id so it can be used with systems that only
 * know the base store node — Åtkomst permissions, Livlig, teams lookups,
 * etc. Store ids that do not end with a known suffix are returned as-is.
 *
 * `012`              → `012`
 * `012#fixa-team`    → `012`
 * `481$fixa-team`    → `481`
 * `012#something`    → `012#something` (unknown suffix, untouched)
 */
export const normalizeFixaTeamStoreId = (storeId: string): string => {
  const matchedSuffix = fixaTeamVirtualStoreSuffixes.find((suffix) =>
    storeId.endsWith(suffix)
  );
  return matchedSuffix
    ? storeId.slice(0, storeId.length - matchedSuffix.length)
    : storeId;
};
